import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Mail, Phone, ChevronRight, ChevronDown } from 'lucide-react';
import { CONTENT, IMAGES, TECH_SHEETS, INSTAGRAM_URL } from '../data/lempoleData';
import SlothLogo, { Wordmark } from './SlothLogo';
import Marquee from './Marquee';
import SectionNav from './SectionNav';
import SignatureSVG from './SignatureSVG';
import DiarioModal from './DiarioModal';
import VigneronModal from './VigneronModal';
import InstagramFeed from './InstagramFeed';
import WhatsAppFAB from './WhatsAppFAB';
import BottleGallery from './BottleGallery';
import RosatoPhotoGrid from './RosatoPhotoGrid';
import CookieBanner from './CookieBanner';
import { useReveal, useParallax, useDataParallax } from '../hooks/useScrollFx';
import { useLenis } from '../hooks/useLenis';
import SlowIcon from './SlowIcon';
import TripticoAnime from './sections/TripticoAnime';
import BreatheInterlude from './sections/BreatheInterlude';
import SoulSection from './sections/SoulSection';
import AmbientPlayer from './AmbientPlayer';

export default function LempoleSite() {
  const [lang, setLang] = useState(() => {
    if (typeof window === 'undefined') return 'it';
    // Italian is the site's home language and is always the default.
    // We deliberately do NOT auto-switch based on the browser locale — many
    // Italian visitors run their OS/browser in English and were being bumped
    // off IT. Foreign visitors change language via the header selector
    // (which writes `?lang=en` or `?lang=fr` to the URL).
    const url = new URLSearchParams(window.location.search).get('lang');
    if (['it', 'en', 'fr'].includes(url)) return url;
    return 'it';
  });
  const [scrolled, setScrolled] = useState(false);
  const [diarioOpen, setDiarioOpen] = useState(false);
  const [vigneronOpen, setVigneronOpen] = useState(false);
  const handleDiarioClose = useCallback(() => setDiarioOpen(false), []);
  const handleVigneronClose = useCallback(() => setVigneronOpen(false), []);
  const t = CONTENT[lang];
  const rootRef = useReveal();
  useDataParallax(rootRef);
  useLenis({ lerp: 0.08 });
  const bottleTrackRef = useRef(null);
  const bottleParallaxRef = useParallax(0.14, bottleTrackRef);
  const heroSectionRef = useRef(null);
  const heroBgDesktopRef = useParallax(0.22, heroSectionRef);
  const heroBgMobileRef = useParallax(0.18, heroSectionRef);
  const navRef = useRef(null);

  useEffect(() => {
    if (typeof window !== 'undefined') localStorage.setItem('lempole_lang', lang);
    document.documentElement.lang = lang;
    const titles = {
      it: 'Lempole — dove il tempo si ferma.',
      en: 'Lempole — where time stands still.',
      fr: 'Lempole — là où le temps s\u2019arrête.',
    };
    document.title = titles[lang] || titles.it;
    // Keep URL in sync with lang state so hreflang / share links stay correct
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const current = params.get('lang');
      if (lang !== 'it' && current !== lang) {
        params.set('lang', lang);
        window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}${window.location.hash}`);
      } else if (lang === 'it' && current) {
        params.delete('lang');
        const q = params.toString();
        window.history.replaceState({}, '', `${window.location.pathname}${q ? '?' + q : ''}${window.location.hash}`);
      }
    }
  }, [lang]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Dom-Pérignon-style header fade: as the reader scrolls through the hero,
  // the centred logo dissolves smoothly (opacity 1 → 0) and eases back in when
  // they scroll back up. Desktop only — the mobile nav is sticky and needs to
  // stay visible for tap access. Uses rAF + direct style writes to avoid
  // re-rendering the whole tree on every scroll pixel.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia('(min-width: 768px)');
    let raf = 0;
    const apply = () => {
      raf = 0;
      const node = navRef.current;
      if (!node) return;
      if (!mql.matches) {
        // Mobile: reset any residual desktop styling.
        node.style.opacity = '';
        node.style.transform = '';
        node.style.pointerEvents = '';
        return;
      }
      // Fade completes across ~65% of the first viewport height so the logo
      // is already gone by the time the "Two Souls" chapter is on screen.
      const distance = window.innerHeight * 0.65;
      const progress = Math.min(1, Math.max(0, window.scrollY / distance));
      // Ease-out cubic: quicker start, gentle finish — matches dom perignon's feel.
      const eased = 1 - Math.pow(1 - progress, 3);
      const opacity = 1 - eased;
      node.style.opacity = String(opacity);
      // Subtle lift so the logo drifts up as it dissolves.
      node.style.transform = `translateY(${-eased * 12}px)`;
      // Once fully faded, disable pointer events so the ghost header can't be clicked.
      node.style.pointerEvents = opacity < 0.05 ? 'none' : '';
    };
    const onScroll = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(apply);
    };
    apply();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', apply);
    mql.addEventListener?.('change', apply);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', apply);
      mql.removeEventListener?.('change', apply);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, []);

  // When we land on the home from another route (e.g. the Diario) with a hash
  // in the URL, react-router doesn't auto-scroll — we do it once the DOM is
  // ready. A tiny timeout lets fonts / images settle so the target lines up.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const hash = window.location.hash.replace('#', '');
    if (!hash) return;
    const scrollToHash = () => {
      const el = document.getElementById(hash);
      if (!el) return;
      if (window.__lenis) window.__lenis.scrollTo(el, { offset: 0 });
      else el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    const id = window.setTimeout(scrollToHash, 250);
    return () => window.clearTimeout(id);
  }, []);

  return (
    <div ref={rootRef} className="App font-body">
      {/* Vertical section navigation */}
      <SectionNav items={[
        { id: 'top', label: lang === 'it' ? 'Rinascita' : 'Rebirth' },
        { id: 'chapter-souls', label: lang === 'it' ? 'Due Anime' : 'Two Souls' },
        { id: 'chapter-manifesto', label: lang === 'it' ? 'Manifesto' : 'Manifesto' },
        { id: 'chapter-label', label: lang === 'it' ? 'Rosato' : 'Rosé' },
        { id: 'chapter-diario', label: lang === 'it' ? 'Diario' : 'Diary' },
        { id: 'chapter-coming', label: lang === 'it' ? 'Il cammino' : 'The journey' },
        { id: 'chapter-venues', label: lang === 'it' ? 'La famiglia' : 'The family' },
      ]} />
      {/* NAV — desktop: centred logo with Dom-Pérignon-style scroll fade.
          Mobile: sticky, solid black bar, centred wordmark only (no sloth). */}
      <nav
        ref={navRef}
        className={`nav-shell nav-shell--desktop-centred ${scrolled ? 'nav-shell--mobile-black' : ''} top-0 left-0 right-0 z-50 flex items-center justify-center px-6 md:px-10 py-2 md:py-4`}
        style={{
          background: scrolled ? 'rgba(122, 53, 32, 0.94)' : 'rgba(0,0,0,0)',
          backdropFilter: scrolled ? 'blur(8px)' : 'none',
          WebkitBackdropFilter: scrolled ? 'blur(8px)' : 'none',
          boxShadow: scrolled ? '0 1px 20px rgba(0,0,0,0.15)' : 'none',
          transition: 'background 0.8s cubic-bezier(0.25, 1, 0.5, 1), box-shadow 0.8s cubic-bezier(0.25, 1, 0.5, 1)'
        }}
      >
        <a href="#top" className="nav-brand flex items-center gap-2">
          {/* Wordmark-only across all breakpoints per client spec — the sloth
              lives in the hero label and on the footer, not in the header. */}
          <Wordmark height={44} className="drop-shadow-[0_1px_6px_rgba(0,0,0,0.35)]" />
        </a>

        {/* Language selector — desktop only. Absolutely positioned so the
            centred wordmark keeps its geometry. Shows the current 2-letter
            code and expands to a small panel on hover / focus. */}
        <div className="nav-lang nav-lang--header hidden md:block" data-testid="nav-lang">
          <button
            type="button"
            className="nav-lang__current"
            aria-haspopup="listbox"
            aria-label={lang === 'it' ? 'Cambia lingua' : lang === 'fr' ? 'Changer de langue' : 'Change language'}
            data-testid="nav-lang-current"
          >
            <span>{lang.toUpperCase()}</span>
            <ChevronDown size={12} strokeWidth={2.5} />
          </button>
          <div
            className="nav-lang__panel"
            role="menu"
            aria-label={lang === 'it' ? 'Seleziona lingua' : lang === 'fr' ? 'Choisir la langue' : 'Select language'}
          >
            {['it', 'en', 'fr'].filter((l) => l !== lang).map((l) => (
              <button
                key={l}
                type="button"
                role="menuitem"
                className="nav-lang__opt"
                onClick={() => setLang(l)}
                data-testid={`nav-lang-${l}`}
              >
                {l.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section ref={heroSectionRef} id="top" data-theme="dark" className="hero">
        {/* Desktop bg */}
        <div ref={heroBgDesktopRef} className="hero-bg hero-bg-desktop hero-bg--parallax" style={{ backgroundImage: `url(${IMAGES.hero})` }} />
        {/* Mobile bg */}
        <div ref={heroBgMobileRef} className="hero-bg hero-bg-mobile hero-bg--parallax" style={{ backgroundImage: `url(${IMAGES.heroMobile})` }} />
        <div className="hero-scrim" />
        <div className="relative z-10 min-h-screen flex items-center">
          <div className="w-full grid md:grid-cols-2 gap-8 px-8 md:px-16 lg:px-24 py-24">
            <div className="text-white" data-parallax-speed="-0.06" style={{ maxWidth: 560 }}>
              <h1 className="font-display text-[1.9rem] md:text-[2.5rem] lg:text-[2.9rem] leading-[1.08]" style={{ letterSpacing: '-0.04em' }}>
                <span className="block">{t.hero.line1}</span>
                <span className="block">{t.hero.line2}</span>
                <span className="block font-display-italic">
                  {t.hero.line3}
                  <span className="hero-mark-upright" aria-hidden="true">…</span>
                </span>
              </h1>
              <p className="font-body text-[1.02rem] md:text-[1.16rem] leading-[1.7] mt-9 opacity-95" style={{ maxWidth: 460 }}>
                {t.hero.body}
              </p>
              <a
                href="#chapter-souls"
                onClick={(e) => {
                  e.preventDefault();
                  const el = document.getElementById('chapter-souls');
                  if (!el) return;
                  // Prefer Lenis' smooth-scroll when available so the CTA
                  // inherits the same buttery inertia as the rest of the page.
                  if (window.__lenis) window.__lenis.scrollTo(el, { offset: 0 });
                  else el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                className="hero-cta mt-10 inline-flex items-center gap-3"
                data-testid="hero-cta"
              >
                <span className="hero-cta__line" aria-hidden="true" />
                <span className="hero-cta__text font-display-italic">{t.hero.cta}</span>
                <ChevronRight size={18} className="hero-cta__arrow" />
              </a>
            </div>
          </div>
        </div>

        {/* scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-white/80 flex flex-col items-center gap-2">
          <span className="font-kicker text-[0.6rem]">SCROLL</span>
          <span className="w-px h-10 bg-white/60 animate-pulse" />
        </div>
      </section>

      {/* CHAPTER 2 — TWO SOULS: Intro + Trittico */}
      <section id="chapter-souls" data-theme="light" className="section-pad" style={{ background: 'var(--lem-ch-souls)' }}>
        <div className="max-w-4xl mx-auto text-center reveal">
          <span className="eyebrow">{t.twoSouls.overline}</span>
          <h2 className="font-display text-[2.2rem] md:text-[3rem] mt-8 leading-[1.1]" style={{ color: 'var(--lem-ink)' }}>
            {lang === 'it' ? (
              <>Una radice profonda. <em className="font-display-italic">Un frutto vibrante.</em></>
            ) : (
              <>A deep root. <em className="font-display-italic">A vibrant fruit.</em></>
            )}
          </h2>
          <p className="font-body text-[1.02rem] md:text-[1.16rem] mt-8 leading-[1.75] max-w-2xl mx-auto" style={{ color: 'var(--lem-ink-soft)' }}>
            {lang === 'it'
              ? 'Tre volti di un unico progetto: la radice che custodisce, il ponte che trasforma, il frutto che si racconta.'
              : 'Three faces of one project: the root that protects, the bridge that transforms, the fruit that tells its story.'}
          </p>
        </div>

        <div className="mt-16 md:mt-20">
          <TripticoAnime t={t} images={IMAGES} />
        </div>
      </section>

      {/* THREE SOULS - split-screen with parallax
          Order: Radice → Frutto → Ponte (Z zig-zag maintained by alternating imageLeft) */}
      <SoulSection
        id="soul-root"
        eyebrow={t.twoSouls.root.eyebrow}
        title={t.twoSouls.root.title}
        subtitle={t.twoSouls.root.subtitle}
        subtitleIcon={
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.3"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            style={{ flexShrink: 0, opacity: 0.85 }}
          >
            <path d="M12 21.5c-4.5-5.2-7-8.6-7-11.7a7 7 0 1 1 14 0c0 3.1-2.5 6.5-7 11.7Z" />
            <circle cx="12" cy="9.8" r="2.4" />
          </svg>
        }
        body={t.twoSouls.root.body}
        image={IMAGES.root}
        imageAlt={lang === 'it' ? 'Terrazzamenti in pietra ricoperti di muschio e vegetazione al Madonnino dei Campacci, Lamole' : 'Stone terraces covered in moss and vegetation at Madonnino dei Campacci, Lamole'}
        imageLeft={false}
        background="var(--lem-ch-souls)"
        imageFilter="saturate(0.78) sepia(0.16) contrast(0.98) brightness(1.02) hue-rotate(-6deg)"
      />

      <SoulSection
        id="soul-pop"
        eyebrow={t.twoSouls.pop.eyebrow}
        title={t.twoSouls.pop.title}
        subtitle={t.twoSouls.pop.subtitle}
        body={t.twoSouls.pop.body}
        image={IMAGES.fruit}
        imageAlt={lang === 'it' ? 'Bottiglia Lempole scolpita nella terra crepata color argilla' : 'Lempole bottle indented into cracked clay earth'}
        imageLeft={true}
        background="var(--lem-ch-souls-alt)"
        imageSlot={
          <img
            src={process.env.PUBLIC_URL + '/pop-slider/anima-pop.webp'}
            alt={lang === 'it' ? 'Etichetta Lempole adagiata sulla terra rossa di Lamole' : (lang === 'fr' ? 'Étiquette Lempole posée sur la terre rouge de Lamole' : 'Lempole label lying on the red earth of Lamole')}
            className="anima-pop-static"
            loading="lazy"
            decoding="async"
            data-testid="anima-pop-image"
          />
        }
      />

      <SoulSection
        id="soul-bridge"
        eyebrow={t.twoSouls.bridge.eyebrow}
        title={t.twoSouls.bridge.title}
        subtitle={t.twoSouls.bridge.subtitle}
        body={t.twoSouls.bridge.body}
        quote={t.twoSouls.bridge.quote}
        image={IMAGES.vigneron}
        imageAlt={lang === 'it' ? 'Raffaello Tiezzi con la famiglia nella vigna al tramonto' : 'Raffaello Tiezzi with his family in the vineyard at sunset'}
        imageLeft={false}
        background="var(--lem-ch-souls)"
        imageObjectPosition="20% 42%"
        parallaxOffset={0.08}
        parallaxStretch={0.06}
        imageFilter="saturate(0.78) sepia(0.16) contrast(0.98) brightness(1.02) hue-rotate(-6deg)"
        linkedin="https://it.linkedin.com/in/raffaello-tiezzi-ab566b13"
        linkedinLabel={lang === 'it' ? 'Raffaello Tiezzi su LinkedIn' : 'Raffaello Tiezzi on LinkedIn'}
        storyLinkLabel={lang === 'it' ? 'la mia storia' : lang === 'fr' ? 'mon histoire' : 'my story'}
        onStoryClick={() => setVigneronOpen(true)}
      />

      {/* CHAPTER 3 — MANIFESTO — the beating heart of the site.
          Deliberately breaks the light-ivory rhythm with a dark terracotta chapter
          so the visitor cannot miss it. Content is framed and compact. */}
      <section id="chapter-manifesto" data-theme="light" className="manifesto-section manifesto-section--light relative" style={{ background: '#ffffff' }}>
        <div className="manifesto-frame reveal" data-parallax-speed="0.06">
          <span className="eyebrow manifesto-eyebrow">
            {t.manifesto.overline}
          </span>
          <h2 className="font-display text-[2rem] md:text-[2.8rem] leading-[1.05] manifesto-title mt-8">
            {(() => {
              const parts = t.manifesto.title.split(' ');
              const last = parts.pop();
              return (
                <>
                  {parts.join(' ')} <em className="font-display-italic">{last}</em>
                </>
              );
            })()}
          </h2>
          <blockquote className="manifesto-quote font-display-italic text-[1.25rem] md:text-[1.55rem] leading-[1.35]">
            <span className="manifesto-quote-mark" aria-hidden="true">&ldquo;</span>
            {t.manifesto.quote.replace(/^["\u201C\u201D]|["\u201C\u201D]$/g, '')}
          </blockquote>
          <p className="font-body text-[1rem] md:text-[1.12rem] manifesto-body">{t.manifesto.lead}</p>
          {t.manifesto.close && (
            <p className="font-body text-[1rem] md:text-[1.12rem] manifesto-body mt-6">{t.manifesto.close}</p>
          )}
          {t.manifesto.cta && (
            <div className="mt-10 md:mt-12 flex justify-center" />
          )}
        </div>
      </section>

      {/* ===== BREATHING INTERLUDE — full-bleed parallax bottle on a twisted vine ===== */}
      <BreatheInterlude
        id="chapter-label"
        image={IMAGES.breatheBottle}
        headline={lang === 'it' ? (<>Dalla vecchia vite,<br/><em className="font-display-italic">il primo sorso.</em></>) : (<>From the ancient vine,<br/><em className="font-display-italic">the first sip.</em></>)}
        caption={lang === 'it' ? 'Un\u2019attesa che diventa gesto.' : 'A wait that becomes a gesture.'}
      />

      {/* CHAPTER 4 — FIRST LABEL (bottle) — split-screen with full-bleed image on the left, white background.
          The image is hidden on mobile because the Breathe interlude above already shows a bottle-with-vine visual. */}
      <section id="chapter-bottle" data-theme="light" className="relative w-full" style={{ background: '#ffffff', minHeight: '92vh' }}>
        <div className="grid grid-cols-1 md:grid-cols-2 items-stretch" style={{ minHeight: '92vh' }}>
          {/* Image side — full-bleed left with primary bottle photo (desktop only) */}
          <div
            ref={bottleTrackRef}
            className="relative overflow-hidden reveal hidden md:block"
            style={{ minHeight: '60vh', background: '#f7edd6' }}
          >
            <img
              ref={bottleParallaxRef}
              src={IMAGES.bottle}
              alt="Bottiglia Lempole Rosato IGT Toscana con ombra rosa"
              className="absolute w-full object-cover"
              style={{
                top: '-8%',
                left: 0,
                height: '116%',
                display: 'block',
                willChange: 'transform',
              }}
            />
          </div>
          {/* Text side — right (full width on mobile) */}
          <div className="flex items-center reveal px-8 md:px-14 lg:px-20 py-16 md:py-24">
            <div style={{ maxWidth: 560 }}>
              <span className="eyebrow">{t.bottle.overline}</span>
              <h3 className="font-display text-[2rem] md:text-[2.8rem] mt-6 leading-[1.05]" style={{ color: 'var(--lem-ink)' }}>
                {(() => {
                  const parts = t.bottle.tagline.split(/\s+/);
                  return parts.map((word, i) => (
                    <React.Fragment key={i}>
                      {i > 0 && ' '}
                      {i === 1 ? <span className="font-display-italic">{word}</span> : word}
                    </React.Fragment>
                  ));
                })()}
              </h3>
              <p className="font-kicker text-[0.82rem] mt-4" style={{ color: 'var(--lem-ink-soft)', letterSpacing: '0.32em' }}>{t.bottle.title}</p>
              <p className="font-body text-[1.02rem] md:text-[1.16rem] mt-7 leading-[1.75]" style={{ color: 'var(--lem-ink-soft)' }}>{t.bottle.body}</p>

              <div className="mt-9 space-y-3" style={{ maxWidth: 520 }}>
                {/* Row 1: 3 badges */}
                <div className="grid grid-cols-3 gap-3">
                  {t.bottle.badges.slice(0, 3).map((b, i) => (
                    <span
                      key={i}
                      className="badge-pill badge-cycle"
                      style={{ transitionDelay: `${0.5 + i * 0.6}s` }}
                    >
                      <span className="top">{b.top}</span>
                      {b.bottom && <span className="bottom">{b.bottom}</span>}
                    </span>
                  ))}
                </div>
                {/* Row 2: 2 badges centered */}
                <div className="grid grid-cols-6 gap-3">
                  {t.bottle.badges.slice(3).map((b, i) => (
                    <span
                      key={i + 3}
                      className="badge-pill badge-cycle"
                      style={{
                        gridColumn: i === 0 ? '2 / span 2' : '4 / span 2',
                        transitionDelay: `${0.5 + (i + 3) * 0.6}s`,
                      }}
                    >
                      <span className="top">{b.top}</span>
                      {b.bottom && <span className="bottom">{b.bottom}</span>}
                    </span>
                  ))}
                </div>
              </div>

              <a
                href={TECH_SHEETS[lang] || TECH_SHEETS.it}
                target="_blank"
                rel="noreferrer"
                download={`lempole-rosato-${lang}.pdf`}
                className="tech-sheet-link mt-10"
                data-testid="rosato-tech-sheet-link"
              >
                <svg className="tech-sheet-link__icon" width="14" height="16" viewBox="0 0 14 16" aria-hidden="true">
                  <path d="M2 1 h6 l4 4 v10 H2 Z" fill="none" stroke="currentColor" strokeWidth="1.1" strokeLinejoin="round"/>
                  <path d="M8 1 v4 h4" fill="none" stroke="currentColor" strokeWidth="1.1" strokeLinejoin="round"/>
                  <path d="M4.5 9 h5 M4.5 12 h5" stroke="currentColor" strokeWidth="0.9" strokeLinecap="round"/>
                </svg>
                <span className="tech-sheet-link__text">{t.bottle.cta}</span>
                <span className="tech-sheet-link__rule" aria-hidden="true" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Rosato photo grid — 1×6 desktop / 2×3 mobile, no zoom */}
      <RosatoPhotoGrid heroSrc={IMAGES.bottle} />

      {/* Raffaello quote section — warm ink backdrop, his voice in typewriter
          type echoing the diary that follows immediately below. */}
      <section data-theme="dark" className="relative section-pad raffaello-section" style={{ background: 'rgb(13, 13, 13)' }}>
        <div className="relative mx-auto text-center reveal content-max">
          <div className="raffaello-quote-mark font-display" aria-hidden="true">&ldquo;</div>
          <p className="typewriter raffaello-body font-caveat">
            {t.quote.body.split('').map((ch, i) => (
              <span
                key={i}
                className="tw-char"
                style={{ transitionDelay: `${0.4 + i * 0.035}s` }}
              >
                {ch}
              </span>
            ))}
          </p>
          <SignatureSVG
            className="signature mt-14"
            data-testid="raffaello-signature"
            style={{
              width: 'min(480px, 62vw)',
              height: 'auto',
              display: 'inline-block',
              color: '#e9dcc4',
            }}
          />
        </div>
      </section>

      {/* CHAPTER 4.5 — DIARIO DI CANTINA (preview of Raffaello's handwritten cellar diary) */}
      <section id="chapter-diario" data-theme="light" className="diario-section relative">
        <div className="diario-section__paper" aria-hidden="true" />
        <div className="relative section-pad">
          <div className="mx-auto text-center reveal content-max">
            <h1 className="diario-section__title" data-testid="diario-section-title">
              {t.diario.title}
            </h1>
          </div>

          {/* Preview text — sits directly on the parchment, no card wrapper */}
          <div className="diario-preview reveal" data-testid="diario-photo-preview" aria-label="Diario di cantina di Raffaello">
            <p className="diario-preview__text font-caveat">
              {t.diario.annata.preview}
            </p>
            <div className="diario-preview__ctas">
              <button
                type="button"
                onClick={() => setDiarioOpen(true)}
                className="hero-cta hero-cta--ink inline-flex items-center gap-3"
                data-testid="diario-read-more-btn"
              >
                <span className="hero-cta__line" aria-hidden="true" />
                <span className="hero-cta__text font-display-italic">{t.diario.readMore}</span>
                <ChevronRight size={18} className="hero-cta__arrow" />
              </button>
              <Link
                to={`/diario${lang === 'it' ? '' : `?lang=${lang}`}`}
                className="hero-cta hero-cta--ink inline-flex items-center gap-3"
                data-testid="diario-blog-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="hero-cta__line" aria-hidden="true" />
                <span className="hero-cta__text font-display-italic">
                  {lang === 'it' ? 'tutti gli articoli del diario' : lang === 'fr' ? 'tous les articles du journal' : 'read the full diary'}
                </span>
                <ChevronRight size={18} className="hero-cta__arrow" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CHAPTER 5 — IL CAMMINO — editorial timeline of the future chapters */}
      <section id="chapter-coming" data-theme="dark" className="section-pad coming-section" style={{ background: '#7f8a72' }}>
        <div className="mx-auto text-center reveal content-max" data-parallax-speed="0.05">
          <span className="eyebrow coming-eyebrow">{t.comingSlow.overline}</span>
          <h2 className="font-display text-[2.2rem] md:text-[3.2rem] leading-[1.05] coming-title mt-6">
            {t.comingSlow.titleLead} <em className="font-display-italic">{t.comingSlow.titleItalic}</em>
          </h2>
          <p className="font-body text-[1.02rem] md:text-[1.16rem] mt-8 leading-[1.75] coming-subtitle">
            {t.comingSlow.subtitle}
          </p>
        </div>

        <div className="coming-timeline max-w-6xl mx-auto mt-16 md:mt-24 reveal">
          {/* Hand-drawn trail SVG behind the timeline — a faint meandering path
              with small waypoints so the "cammino" metaphor lives visually. */}
          <svg
            className="coming-trail"
            viewBox="0 0 1200 340"
            preserveAspectRatio="none"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              className="coming-trail__path"
              d="M 40 240
                 C 140 200, 200 90, 300 110
                 S 460 240, 570 200
                 S 780 90, 890 130
                 S 1090 240, 1160 190"
              fill="none"
              stroke="#f2c1a8"
              strokeOpacity="0.55"
              strokeWidth="2"
              strokeLinecap="round"
            />
            {/* Waypoint dots */}
            <circle className="coming-trail__dot" cx="140"  cy="176" r="3" fill="#f2c1a8" fillOpacity="0.5" />
            <circle className="coming-trail__dot" cx="425"  cy="212" r="3" fill="#f2c1a8" fillOpacity="0.5" />
            <circle className="coming-trail__dot" cx="720"  cy="140" r="3" fill="#f2c1a8" fillOpacity="0.5" />
            <circle className="coming-trail__dot" cx="1010" cy="215" r="3" fill="#f2c1a8" fillOpacity="0.5" />
            {/* Small compass rose in the top-right corner */}
            <g className="coming-trail__compass" transform="translate(1090 62)" stroke="#f2c1a8" strokeOpacity="0.4" fill="none" strokeWidth="1.2">
              <circle r="18" />
              <path d="M0 -14 L 0 14 M -14 0 L 14 0" />
              <path d="M0 -18 L 3 -11 L 0 -14 L -3 -11 Z" fill="#f2c1a8" fillOpacity="0.4" />
            </g>
            {/* Two footprint pairs mid-trail */}
            <g className="coming-trail__foot" transform="translate(560 246) rotate(-14)" fill="#f2c1a8" fillOpacity="0.5">
              <ellipse cx="0" cy="0" rx="3.5" ry="5" />
              <ellipse cx="8" cy="4" rx="3.5" ry="5" />
            </g>
            <g className="coming-trail__foot" transform="translate(830 108) rotate(-8)" fill="#f2c1a8" fillOpacity="0.5">
              <ellipse cx="0" cy="0" rx="3.5" ry="5" />
              <ellipse cx="8" cy="4" rx="3.5" ry="5" />
            </g>
          </svg>

          {/* Dashed connector — removed in favour of the hand-drawn trail below */}

          {t.comingSlow.items.map((it, idx) => (
            <article
              key={idx}
              className={`coming-card reveal coming-card--${idx % 2 === 0 ? 'up' : 'down'}`}
              style={{ transitionDelay: `${idx * 140}ms` }}
              data-parallax-speed={idx % 2 === 0 ? 0.07 : -0.05}
              data-testid={`coming-card-${it.icon}`}
            >
              <span className="coming-card__num">{String(idx + 1).padStart(2, '0')}</span>
              <SlowIcon name={it.icon} className="coming-card__icon" />
              <h3 className="coming-card__name font-display-italic">{it.name}</h3>
              <p className="coming-card__desc font-body">{it.desc}</p>
              <span className="coming-card__year font-kicker">{t.comingSlow.tag}</span>
              <span className="coming-card__bar" aria-hidden="true" />
            </article>
          ))}

          {/* Sloth walker that trails the pen along the drawn path from left
              to right, then turns to look back at the memory of the journey. */}
          <span className="coming-walker-wrap" aria-hidden="true">
            <span className="coming-walker-turn">
              <img
                src={process.env.PUBLIC_URL + '/favicon-sloth.png'}
                alt=""
                className="coming-walker"
              />
            </span>
          </span>
        </div>
      </section>

      {/* VENUES — social proof + business CTA (contact us) */}
      <section id="chapter-venues" className="section-pad venues-section" style={{ background: '#ffffff' }}>
        <div className="mx-auto text-center reveal content-max">
          <span className="eyebrow venues-eyebrow">{t.venues.overline}</span>
          <h2 className="font-display text-[2.2rem] md:text-[3.2rem] leading-[1.05] venues-title mt-6">
            {t.venues.titleLead} <em className="font-display-italic">{t.venues.titleItalic}</em>
          </h2>
          <p className="font-body text-[1.02rem] md:text-[1.16rem] mt-8 leading-[1.75] venues-body">
            {t.venues.body}
          </p>
        </div>

        {/* Playful masonry-ish gallery of social proof photos */}
        <div className="venues-gallery max-w-6xl mx-auto mt-14 md:mt-20 reveal">
          <figure className="venues-photo venues-photo--a">
            <img src={process.env.PUBLIC_URL + '/venues/bottles-iris.jpg'} alt="Bottiglie Lempole tra iris viola in vigna" loading="lazy" />
          </figure>
          <figure className="venues-photo venues-photo--b">
            <img src={process.env.PUBLIC_URL + '/venues/grapes-vine.jpg'} alt="Grappoli maturi sulla vite di Lamole" loading="lazy" />
          </figure>
          <figure className="venues-photo venues-photo--c">
            <img src={process.env.PUBLIC_URL + '/venues/kids-harvest.jpg'} alt="Bambini che aiutano nella vendemmia" loading="lazy" />
          </figure>
          <figure className="venues-photo venues-photo--d">
            <img src={process.env.PUBLIC_URL + '/venues/basket.jpg'} alt="Cesto di uva appena raccolta con forbici da vendemmia" loading="lazy" />
          </figure>
        </div>

        {/* Invitation — flat editorial block directly on the section */}
        <div className="venues-invite max-w-3xl mx-auto mt-16 md:mt-24 text-center reveal">
          <h3 className="font-display venues-invite__title">{t.venues.inviteTitle}</h3>
          <p className="font-body venues-invite__body">{t.venues.inviteBody}</p>
          <div className="venues-invite__ctas">
            <a
              href={`mailto:${t.venues.emailTo}?subject=${encodeURIComponent(t.venues.inviteTitle)}`}
              className="hero-cta hero-cta--ink inline-flex items-center gap-3"
              data-testid="venues-cta-email"
            >
              <span className="hero-cta__line" aria-hidden="true" />
              <Mail size={18} />
              <span className="hero-cta__text font-display-italic">{t.venues.ctaEmail}</span>
              <ChevronRight size={18} className="hero-cta__arrow" />
            </a>
            <a
              href={`https://wa.me/${t.venues.whatsappNumber.replace(/[^0-9]/g,'')}?text=${encodeURIComponent(t.venues.inviteTitle)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="hero-cta hero-cta--ink inline-flex items-center gap-3"
              data-testid="venues-cta-whatsapp"
            >
              <span className="hero-cta__line" aria-hidden="true" />
              <Phone size={18} />
              <span className="hero-cta__text font-display-italic">{t.venues.ctaWhatsapp}</span>
              <ChevronRight size={18} className="hero-cta__arrow" />
            </a>
          </div>
        </div>
      </section>

      {/* SINTONIE — hidden until partner logos are provided */}
      {IMAGES.sintonie.length > 0 && (
      <section className="section-pad" style={{ background: 'var(--lem-ch-sintonie)' }}>
        <div className="mx-auto text-center reveal content-max">
          <h2 className="font-display-italic text-[2rem] md:text-[2.8rem] leading-[1.1]" style={{ color: 'var(--lem-ink)' }}>{t.sintonie.title}</h2>
          <p className="font-body text-[1.02rem] md:text-[1.16rem] mt-8 leading-[1.75]" style={{ color: 'var(--lem-ink-soft)' }}>{t.sintonie.body}</p>
        </div>
        <div className="mt-16 reveal">
          <Marquee speed={55}>
            {IMAGES.sintonie.map((src, i) => (
              <div key={i} className="logo-cell" style={{ minWidth: 180 }}>
                <img src={src} alt={`Sintonia ${i + 1}`} loading="lazy" />
              </div>
            ))}
          </Marquee>
        </div>
      </section>
      )}

      {/* Instagram feed — currently hidden, will be re-enabled with real posts */}
      {false && <InstagramFeed />}

      {/* FOOTER — slightly contrasted per spec */}
      <footer className="pt-24 pb-10 px-6 md:px-16" style={{ background: '#1a1512', color: '#f2e9d8' }}>
        <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-14 items-start">
          <div>
            <div className="flex items-center gap-1">
              <Wordmark height={56} />
            </div>
            <p className="font-display-italic mt-6 text-[1.1rem] opacity-90">{t.footer.tagline}</p>
          </div>

          <div className="font-body text-[0.95rem] leading-[1.9]">
            <p className="opacity-90">{t.footer.copyright}</p>
            <p className="opacity-90">{t.footer.address}</p>
            <p className="opacity-90">{t.footer.vat}</p>
            <p className="opacity-80 mt-3 text-sm">{t.footer.rights}</p>
            <div className="footer-legal-links mt-3" data-testid="footer-legal-links">
              <a
                href="https://www.iubenda.com/privacy-policy/50690964"
                target="_blank"
                rel="noopener noreferrer"
                className="footer-legal-link"
                data-testid="footer-privacy-link"
              >{t.footer.privacy}</a>
              <span className="footer-legal-sep" aria-hidden="true">·</span>
              <a
                href="https://www.iubenda.com/privacy-policy/50690964/cookie-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="footer-legal-link"
                data-testid="footer-cookie-link"
              >{t.footer.cookie}</a>
              <span className="footer-legal-sep" aria-hidden="true">·</span>
              <Link
                to={`/termini${lang === 'it' ? '' : `?lang=${lang}`}`}
                className="footer-legal-link"
                data-testid="footer-terms-link"
              >{t.footer.terms}</Link>
            </div>
          </div>

          <div>
            <div className="flex items-center gap-4">
              <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" aria-label="Instagram" className="w-11 h-11 rounded-full border border-current flex items-center justify-center hover:bg-white/10 transition-all duration-500">
                <Instagram size={18} />
              </a>
              <a href={`mailto:${t.footer.email}`} aria-label="Email" className="w-11 h-11 rounded-full border border-current flex items-center justify-center hover:bg-white/10 transition-all duration-500">
                <Mail size={18} />
              </a>
              <a href={`tel:${t.footer.phone.replace(/\s/g,'')}`} aria-label="Phone" className="w-11 h-11 rounded-full border border-current flex items-center justify-center hover:bg-white/10 transition-all duration-500">
                <Phone size={18} />
              </a>
            </div>
            <a href={`mailto:${t.footer.email}`} className="block mt-6 text-sm opacity-90 hover:opacity-100 font-body">{t.footer.email}</a>
            <a href={`tel:${t.footer.phone.replace(/\s/g,'')}`} className="block mt-1 text-sm opacity-90 hover:opacity-100 font-body">{t.footer.phone}</a>
            <p className="mt-6 text-sm opacity-90 font-body">{t.footer.handle}</p>

            <div className="flex items-center gap-3 mt-8" data-testid="footer-lang-switcher">
              <button onClick={() => setLang('it')} className={`lang-btn ${lang === 'it' ? 'active' : ''}`} style={{ color: '#f2e9d8' }}>IT</button>
              <span className="opacity-50">|</span>
              <button onClick={() => setLang('en')} className={`lang-btn ${lang === 'en' ? 'active' : ''}`} style={{ color: '#f2e9d8' }}>EN</button>
              <span className="opacity-50">|</span>
              <button onClick={() => setLang('fr')} className={`lang-btn ${lang === 'fr' ? 'active' : ''}`} style={{ color: '#f2e9d8' }}>FR</button>
            </div>
          </div>
        </div>

        <div className="soft-divider mt-14" style={{ background: 'linear-gradient(90deg, transparent, rgba(242,233,216,0.3), transparent)' }} />

        {/* Legal / responsible drinking notice */}
        <div className="footer-disclaimer" data-testid="footer-disclaimer">
          <span className="footer-disclaimer__badge" aria-label="18+">18<span className="footer-disclaimer__plus">+</span></span>
          <div className="footer-disclaimer__lines">
            <p className="footer-disclaimer__line font-display-italic">
              <svg className="footer-disclaimer__icon" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                <path d="M6 3 h12 l-1.4 8.4 A4.6 4.6 0 0 1 12 15.2 A4.6 4.6 0 0 1 7.4 11.4 Z" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/>
                <path d="M12 15.2 V21" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
                <path d="M9 21 H15" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
              </svg>
              {t.footer.disclaimer1}
            </p>
            <p className="footer-disclaimer__line footer-disclaimer__line--sub">
              {t.footer.disclaimer2}
              <span className="footer-disclaimer__age">{t.footer.ageWarn}</span>
            </p>
          </div>
        </div>

        <p className="text-center font-kicker text-[0.65rem] opacity-70 mt-6">Lempole — Slow Wine Philosophy</p>
      </footer>

      <DiarioModal open={diarioOpen} onClose={handleDiarioClose} lang={lang} />
      <VigneronModal open={vigneronOpen} onClose={handleVigneronClose} lang={lang} />
      <WhatsAppFAB />
      <AmbientPlayer />
      <CookieBanner />
    </div>
  );
}
